import { FocusScope } from '@radix-ui/react-focus-scope';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';
import { classNames } from '~/utils/styles';
import type { FullScreenPanelProps } from './FullScreenPanel.types';
import type { FullScreenPanelPhase, PanelAtSource } from './useFullScreenPanel';
import { fullScreenPanelPhaseEnum, useFullScreenPanel } from './useFullScreenPanel';

const MOTION = 'duration-[320ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none';

// Lifted, the panel is laid out once at its place in the window, padding included. Between the page and the window it
// is moved and clipped onto the section's place in the page: only its transform and clip-path animate, which lay
// nothing out, so a large content does not slow the lift down. The panel's classes change between phases, so none of
// them sets a custom property: its descendants inherit custom properties, and a change would restyle the whole
// content. Hence a plain `box-shadow` rather than `shadow-*` and `ring-*`, and the starting place as inline values.
const LANDED_PANEL =
  'fixed left-[1.5vw] top-[1.5vh] z-20 h-[97vh] w-[97vw] overflow-hidden bg-white px-6 py-5 outline-hidden [box-shadow:0_0_0_1px_theme(colors.neutral.300),theme(boxShadow.xl)]';

const MOVING_PANEL = classNames(LANDED_PANEL, 'transition-[transform,clip-path]', MOTION);

const PANEL_CLASS_NAMES: Record<FullScreenPanelPhase, string> = {
  [fullScreenPanelPhaseEnum.enum.inPage]: '',
  // No transition: the panel takes its starting place at once.
  [fullScreenPanelPhaseEnum.enum.lifting]: LANDED_PANEL,
  // The clip leaves room for the shadow; it is an inset too, so the clip of the starting place transitions to it.
  [fullScreenPanelPhaseEnum.enum.open]: classNames(MOVING_PANEL, '[clip-path:inset(-3rem)]'),
  [fullScreenPanelPhaseEnum.enum.closing]: MOVING_PANEL,
};

const preventAutoFocus = (event: Event) => event.preventDefault();

// What the content opens renders in a portal outside the panel: popovers, menus and selects in Radix's popper
// wrapper, and dialogs.
const LAYER_SELECTOR = '[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"]';

/** Between the page and the window, the panel sits on its place in the page. */
function atSourceStyle(phase: FullScreenPanelPhase, atSource: PanelAtSource | null): CSSProperties | undefined {
  const isAtSource = phase === fullScreenPanelPhaseEnum.enum.lifting || phase === fullScreenPanelPhaseEnum.enum.closing;
  if (!isAtSource || !atSource) {
    return undefined;
  }
  const { clipBottom, clipLeft, clipRight, clipTop, translateX, translateY } = atSource;
  return {
    clipPath: `inset(${clipTop}px ${clipRight}px ${clipBottom}px ${clipLeft}px)`,
    transform: `translate(${translateX}px, ${translateY}px)`,
  };
}

/**
 * Lifts a section of the page into a panel covering 97% of the window, over a scrim, and puts it back. The panel
 * grows from the section's place and shrinks back to it, uncovering more of its content as it grows. The content is
 * never mounted again, so it keeps its state and its scroll positions, and the section keeps its height in the page
 * meanwhile.
 */
export function FullScreenPanel({
  open,
  onClose,
  closeOnEscape = true,
  footer,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
}: FullScreenPanelProps) {
  const placeholderRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { atSource, phase, sourceRect } = useFullScreenPanel(open, placeholderRef, panelRef, contentRef);
  const isLifted = phase !== fullScreenPanelPhaseEnum.enum.inPage;
  const isLanded = phase === fullScreenPanelPhaseEnum.enum.open;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape && !event.defaultPrevented) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeOnEscape, onClose, open]);

  // The page under the scrim is out of reach, so focus that lands there (Shift+Tab from the panel itself, say) comes
  // back. Declared before the effect that gives the focus back on close, so it lets that focus go.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleFocusIn = ({ target }: FocusEvent) => {
      const panel = panelRef.current;
      if (!panel || !(target instanceof Element) || panel.contains(target) || target.closest(LAYER_SELECTOR)) {
        return;
      }
      panel.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus({ preventScroll: true });
    }
    return () => {
      if (returnFocusTo?.isConnected) {
        returnFocusTo.focus({ preventScroll: true });
      }
    };
  }, [open]);

  return (
    <div ref={placeholderRef} style={isLifted ? { minHeight: sourceRect.height } : undefined}>
      {isLifted && (
        <div
          aria-hidden
          className={classNames(
            'fixed inset-0 z-20 bg-neutral-1000/30 transition-opacity',
            MOTION,
            isLanded ? 'opacity-100' : 'opacity-0',
          )}
          data-testid="full-screen-panel-scrim"
          onClick={onClose}
        />
      )}

      {/* Lifted, Tab cycles through the panel; a popover, menu or dialog the content opens pauses that while it holds
          the focus. Not `trapped`: that trap moves the focus to the panel when the content changes while no control
          has the focus, as happens between two cells when the first commits its entry on blur. The scope stays
          mounted in the page, so lifting never mounts the content again, and the effects above move the focus. */}
      <FocusScope asChild loop={isLifted} onMountAutoFocus={preventAutoFocus} onUnmountAutoFocus={preventAutoFocus}>
        <section
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-modal={isLifted || undefined}
          className={classNames('flex flex-col', PANEL_CLASS_NAMES[phase])}
          data-phase={phase}
          ref={panelRef}
          role={isLifted ? 'dialog' : undefined}
          style={atSourceStyle(phase, atSource)}
          tabIndex={isLifted ? -1 : undefined}
        >
          <div className={classNames('flex min-h-0 flex-1 flex-col', className)} ref={contentRef}>
            {children}
          </div>

          {/* Laid out from the start of the lift, so it only fades in: the clip uncovers it as the panel grows. */}
          {isLifted && footer && (
            <div
              className={classNames(
                'flex-none border-t border-neutral-300 pt-3.5 transition-opacity',
                MOTION,
                isLanded ? 'opacity-100' : 'opacity-0',
              )}
            >
              {footer}
            </div>
          )}
        </section>
      </FocusScope>
    </div>
  );
}
